import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Upload, Trash2 } from 'lucide-react';
import { Button, Pill } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';

/**
 * PolicyDocumentsPanel — the V1 "Policy Documents" upload surface restored on the
 * unified skill card (V1 SkillCardBuild rendered PolicyDocumentPanel; native V2
 * uses SkillLinkedResourcePanel + an upload modal). Self-contained: lists, uploads
 * (advisory RAG corpus), and deletes documents for a policy_skill via the SHARED
 * backend (api.listPolicyDocuments / uploadPolicyDocument / deletePolicyDocument).
 * These documents are what the Configure → "Extract & Generate" wizard step reads
 * to mine enforcement rules — hence the wizard's "upload on the skill card first".
 *
 * @param {string}   props.skillId
 * @param {boolean}  [props.canEdit]   gate upload/delete (false → read-only list)
 * @param {Function} [props.onChanged] called after upload/delete
 */
export default function PolicyDocumentsPanel({ skillId, canEdit = true, onChanged }) {
  const toastCtx = useToast();
  const addToast = (arg) => toastCtx?.addToast?.(arg);
  const fileRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    if (!skillId) return;
    setLoading(true);
    try {
      const res = await api.listPolicyDocuments?.(skillId);
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setDocs(list);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [skillId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadPolicyDocument(skillId, file, 'advisory', 0);
      addToast({ tone: 'success', title: 'Document uploaded', description: `${file.name} — indexed as advisory RAG.` });
      await load();
      onChanged?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Upload failed', description: err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (doc) => {
    setBusy(true);
    try {
      const res = await api.deletePolicyDocument?.(skillId, doc.id);
      if (res && res.ok === false) throw new Error(res.error || 'Delete failed');
      addToast({ tone: 'info', title: 'Document removed' });
      setConfirmDel(null);
      await load();
      onChanged?.();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Delete failed', description: err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted/40 mb-2.5">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="v2-focus-ring w-full flex items-center gap-2 px-2.5 py-2 text-left">
        <FileText size={13} className="text-v2-cyan" />
        <span className="text-[11.5px] font-semibold text-v2-ink-secondary">Policy Documents</span>
        <Pill tone="muted" size="sm">{loading ? '…' : docs.length}</Pill>
        <span className="ml-auto text-[10.5px] text-v2-ink-muted">{open ? 'Hide' : 'Manage'}</span>
      </button>

      {open && (
        <div className="px-2.5 pb-2.5 space-y-2">
          <p className="text-[10.5px] text-v2-ink-muted m-0">
            Advisory RAG corpus this policy consults, and the source the Configure → Extract step mines for rules.
          </p>
          {loading ? (
            <p className="text-[11px] text-v2-ink-muted m-0">Loading…</p>
          ) : docs.length === 0 ? (
            <p className="text-[11px] text-v2-ink-muted m-0">No documents yet.</p>
          ) : (
            <ul className="space-y-1 m-0 p-0 list-none">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-[11.5px] border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface">
                  <FileText size={11} className="text-v2-ink-muted shrink-0" />
                  <span className="text-v2-ink truncate flex-1">{d.fileName || d.filename || d.name || d.id}</span>
                  {d.chunkCount != null && <span className="text-[10px] text-v2-ink-muted">{d.chunkCount} chunks</span>}
                  {(d.status) && <Pill tone={d.status === 'ready' || d.status === 'parsed' ? 'success' : 'muted'} size="sm">{d.status}</Pill>}
                  {canEdit && (confirmDel === d.id ? (
                    <span className="flex items-center gap-1">
                      <Button variant="danger" size="sm" disabled={busy} onClick={() => remove(d)}>Delete</Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmDel(null)}>Cancel</Button>
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-v2-danger" iconLeft={<Trash2 size={11} />}
                      disabled={busy} onClick={() => setConfirmDel(d.id)} title="Remove document" />
                  ))}
                </li>
              ))}
            </ul>
          )}
          {canEdit && (
            <div>
              <input ref={fileRef} type="file" className="hidden"
                accept=".pdf,.txt,.md,.doc,.docx,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onPick} disabled={busy} />
              <Button variant="secondary" size="sm" iconLeft={<Upload size={12} />}
                loading={busy} disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? 'Uploading…' : 'Upload document'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
